import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { uuidv4 } from "../../utils/helpers";
import logo from "../../static/images/baptists-on-mission-logo.png";
import styles from "./SideNav.module.css";

// Matches on path segments rather than exact equality, so a nested/child
// route (e.g. /admin/users/87) still keeps its parent nav item (/admin/users)
// highlighted. Guards against false positives like /admin/users matching
// /admin/users-archive.
// `exact` is for index-style links (e.g. "/", "/admin") that sit above
// sibling nav items in the URL tree but aren't meant to stay highlighted
// once you've navigated into one of those siblings.
const isActivePath = (pathname, itemPath, exact = false) => {
  if (exact) return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
};

const SideBarLink = ({ item, showIndicator = false, onNavigate }) => {
  const loc = useLocation();
  return (
    <Link
      to={item.path}
      onClick={() => onNavigate?.()}
      className={
        isActivePath(loc.pathname, item.path, item.exact)
          ? `${styles.navLink} ${styles.active}`
          : styles.navLink
      }
    >
      {item.label}
      {showIndicator && <span className={styles.alertDot} title="Background check required">1</span>}
    </Link>
  );
};

// Role ID legend:
//   8  = BOM Super Administrator  (full access)
//   32 = BOM Administrator        (no Payments, no Settings)
//   33 = BOM Finance              (Events, Users, Payments, Reporting)
//   24 = BOM Check-In Staff       (Check-In only)
//   35 = BOM Event Administrator  (Events, Users, Certifications, Check-In, Reporting)
//   38 = BOM Event Coordinator    (Events, Users)

{/* ── Role ID legend ──────────────────────────────────────────────
             32/8  = BOM Super Administrator  (full access)
             33/32 = BOM Administrator        (no Payments, no Settings)
             285/33 = BOM Finance              (Events, Users, Payments, Reporting)
             38/24 = BOM Check-In Staff       (Check-In only)
             35 = BOM Event Administrator  (Events, Users, Certifications, Check-In, Reporting)
             18/38 = BOM Event Coordinator    (Events, Users)
           ──────────────────────────────────────────────────────────────── */}

const AdminLinks = ({ roles, onNavigate }) => {
  const sections = [
    {
      heading: "Dashboard",
      links: [
        // All admin roles see Home. exact: true so it doesn't stay highlighted
        // once you've navigated into a nested route like /admin/users/87.
        { label: "Home", path: "/admin", exact: true, allowedRoles: [32, 33, 285, 38, 35, 18] },
      ],
    },
    {
      heading: "Management",
      links: [
        { label: "Events",         path: "/admin/events",         allowedRoles: [32, 33, 285, 35, 18] },
        { label: "Users",          path: "/admin/users",          allowedRoles: [32, 33, 285, 35, 18] },
        { label: "Payments",       path: "/admin/finances",       allowedRoles: [32, 285] },
        { label: "Certifications", path: "/admin/certifications", allowedRoles: [32, 33, 35] },
        { label: "Roles",          path: "/admin/roles",          allowedRoles: [32, 33] },
      ],
    },
    {
      heading: "Operations",
      links: [
        { label: "Check-In",  path: "/admin/checkin",   allowedRoles: [32, 33, 35, 38] },
        { label: "Reporting", path: "/admin/reporting", allowedRoles: [32, 33, 35, 285] },
      ],
    },
    {
      heading: "System",
      links: [
        { label: "Settings", path: "/admin/settings", allowedRoles: [32] },
      ],
    },
  ];

  return (
    <>
      {sections.map((section) => {
        const visibleLinks = section.links.filter((i) =>
          roles.find((x) => i.allowedRoles?.includes(x.RoleID))
        );
        if (visibleLinks.length === 0) return null;
        return (
          <div className={styles.userLinks} key={section.heading}>
            <div className={styles.menuHeading}>{section.heading}</div>
            {visibleLinks.map((i) => (
              <SideBarLink item={i} key={uuidv4()} onNavigate={onNavigate} />
            ))}
          </div>
        );
      })}
    </>
  );
};

const UserLinks = ({ user, backgroundCheckReminder, onNavigate }) => {
  const isLoggedIn = user && user !== undefined;
  const sections = [
    {
      heading: "My Activity",
      links: [
        { label: "Find Opportunities", path: "/events", exact: true, requiresUser: false },
        { label: "My Reservations & Payments", path: "/myreserved", requiresUser: true },
        { label: "My Certifications", path: "/mycertifications", requiresUser: true },
      ],
    },
    {
      heading: "Account",
      links: [
        { label: "Account Information", path: "/profile", requiresUser: true },
      ],
    },
  ];

  return (
    <>
      {sections.map((section) => {
        const visibleLinks = section.links.filter(
          (i) => i.requiresUser === false || (i.requiresUser === true && isLoggedIn === true)
        );
        if (visibleLinks.length === 0) return null;
        return (
          <div className={styles.userLinks} key={section.heading}>
            <div className={styles.menuHeading}>{section.heading}</div>
            {visibleLinks.map((i) => (
              <SideBarLink
                item={i}
                key={uuidv4()}
                onNavigate={onNavigate}
                showIndicator={Boolean(backgroundCheckReminder?.missingDate && i.path === '/profile')}
              />
            ))}
            {backgroundCheckReminder?.missingDate && section.heading === 'Account' && (
              <div className={styles.bgReminderBox}>
                Complete your <strong>background check</strong>
                <br />
                before participating in opportunities.
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

const SideNav = ({ user, profilePicture, backgroundCheckReminder, onNavigate }) => {
  const roles = user?.roles != null ? user.roles : [];
  const adminRolesList = [32, 33, 285, 38, 35, 18];
  const hasMatch = roles.some(obj => adminRolesList.includes(obj.RoleID));

  return (
    <span>
      <nav className={styles.sideNav} id="sidenavAccordion">
        <div className={styles.siteBrand}>
          <NavLink to="/events">
            <img src={logo} alt="Baptists On Mission" />
          </NavLink>
        </div>
        {hasMatch && (
          <AdminLinks roles={roles} onNavigate={onNavigate} />
        )}
        <UserLinks user={user} backgroundCheckReminder={backgroundCheckReminder} onNavigate={onNavigate} />
        {user && (
          <div className={styles.sideNavFooter}>
            {profilePicture ? (
              <img
                src={profilePicture}
                alt="Profile"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  marginRight: 8,
                }}
              />
            ) : (
              <div
                className="profile-img"
                data-initials={`${user.user?.FirstName?.charAt(0) ?? ''} ${user.user?.LastName?.charAt(0) ?? ''}`}
              ></div>
            )}
            <div className="profile-name">{`${user.user.FirstName} ${user.user.LastName}`}</div>
          </div>
        )}
      </nav>
    </span>
  );
};

export default SideNav;